import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { LayoutDashboard, Target, ShieldAlert, Gauge, ChevronDown } from "lucide-react";

const BAND = { green: "success", amber: "warning", red: "destructive" };

function Stat({ label, value, sub, testid }) {
  return (
    <div className="card-flat p-4" data-testid={testid}>
      <div className="label-eyebrow text-[10px] mb-1">{label}</div>
      <div className="font-display text-3xl font-bold">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function Dist({ dist }) {
  const total = (dist.green || 0) + (dist.amber || 0) + (dist.red || 0) || 1;
  const seg = [["green", "#00E676"], ["amber", "#FFB800"], ["red", "#FF5252"]];
  return (
    <div className="flex h-2 w-full rounded-full overflow-hidden bg-surface">
      {seg.map(([k, c]) => (dist[k] ? <div key={k} style={{ width: `${(dist[k] / total) * 100}%`, background: c }} /> : null))}
    </div>
  );
}

function ScoringTab() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/supply-chain/scoring").then((r) => setData(r.data)).catch(() => setData({ suppliers: [] })); }, []);
  if (!data) return <div className="p-6 text-muted-foreground font-mono text-sm">Loading scoring…</div>;
  return (
    <div className="space-y-3" data-testid="sc-scoring">
      <div className="card-flat p-3 text-[11px] text-muted-foreground">{data.note}</div>
      {data.suppliers.map((s) => (
        <div key={s.supplier_id} className="card-flat p-4" data-testid={`sc-score-${s.supplier_id}`}>
          <button className="w-full flex items-center gap-3 flex-wrap text-left" onClick={() => setOpen(open === s.supplier_id ? null : s.supplier_id)}>
            <div className="flex-1 min-w-[180px]">
              <div className="font-display font-bold text-sm">{s.supplier_name}</div>
              <div className="text-[10px] font-mono text-muted-foreground">{s.country} · S${(s.spend_sgd || 0).toLocaleString()} · {s.product_count} product(s)</div>
            </div>
            <div className="text-right">
              <div className="text-[9px] text-muted-foreground uppercase">Performance</div>
              <div className="font-display text-2xl font-bold">{s.overall_score}</div>
            </div>
            <Badge variant={BAND[s.band]} className="text-[9px] uppercase">{s.band}</Badge>
            <div className="text-right w-24">
              <div className="text-[9px] text-muted-foreground uppercase">Data confidence</div>
              <div className="font-mono text-sm font-bold">{s.data_confidence ?? "—"}</div>
            </div>
            <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open === s.supplier_id ? "rotate-180" : ""}`} />
          </button>
          {open === s.supplier_id && (
            <div className="mt-3 pt-3 border-t border-border space-y-2">
              <div className="text-[10px] text-muted-foreground italic">{s.data_confidence_note}</div>
              {Object.entries(s.dimensions).map(([k, d]) => (
                <div key={k} className="flex items-start gap-3" data-testid={`sc-dim-${k}`}>
                  <div className="w-40 shrink-0">
                    <div className="text-xs font-medium">{d.label}</div>
                    <div className="text-[9px] text-muted-foreground font-mono">weight {Math.round(d.weight * 100)}% · +{d.contribution}</div>
                  </div>
                  <div className="flex-1">
                    <div className="h-1.5 w-full rounded-full bg-surface overflow-hidden">
                      <div className="h-full rounded-full bg-accent" style={{ width: `${d.score}%` }} />
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1">{d.reason}</div>
                  </div>
                  <div className="font-mono text-sm font-bold w-8 text-right">{d.score}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function RiskCbamTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/supply-chain/risk-domains").then((r) => setData(r.data)).catch(() => setData({ esg_risk: [], cbam: [] })); }, []);
  if (!data) return <div className="p-6 text-muted-foreground font-mono text-sm">Loading…</div>;
  const LVL = { high: "destructive", medium: "warning", low: "outline" };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="sc-risk-cbam">
      <div className="card-flat p-5">
        <div className="label-eyebrow mb-3 flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-accent" /> Supply-chain ESG risk</div>
        <div className="space-y-2">
          {data.esg_risk.map((r) => (
            <div key={r.supplier_id} className="p-3 rounded-sm border border-border bg-surface" data-testid={`sc-risk-${r.supplier_id}`}>
              <div className="text-sm font-medium">{r.supplier_name} <span className="text-muted-foreground font-normal text-[11px]">· {r.country}</span></div>
              <div className="flex gap-1.5 flex-wrap mt-1.5">
                {r.flags.map((f, i) => <Badge key={i} variant={LVL[f.level]} className="text-[8px] uppercase">{f.area} · {f.level}</Badge>)}
              </div>
            </div>
          ))}
          {!data.esg_risk.length && <div className="text-muted-foreground text-sm py-6 text-center">No supply-chain ESG risk flags.</div>}
        </div>
      </div>
      <div className="card-flat p-5">
        <div className="label-eyebrow mb-3 flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-[#FFB800]" /> CBAM regulatory domain</div>
        <div className="space-y-2">
          {data.cbam.map((c, i) => (
            <div key={i} className="p-3 rounded-sm border border-border bg-surface" data-testid={`sc-cbam-${i}`}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">{c.product_name} <span className="text-muted-foreground font-normal text-[11px]">· {c.supplier_name}</span></div>
                <div className="font-mono text-sm font-bold">{c.embedded_tco2e} <span className="text-[9px] text-muted-foreground">tCO₂e</span></div>
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">{c.geography} · {c.status}</div>
            </div>
          ))}
          {!data.cbam.length && <div className="text-muted-foreground text-sm py-6 text-center">No CBAM-relevant lines.</div>}
        </div>
        <div className="text-[10px] text-muted-foreground mt-3 italic">{data.note}</div>
      </div>
    </div>
  );
}

export default function ControlTower() {
  const [ct, setCt] = useState(null);
  useEffect(() => { api.get("/supply-chain/control-tower").then((r) => setCt(r.data)).catch(() => setCt({ empty: true })); }, []);
  if (!ct) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading Control Tower…</div>;

  return (
    <div className="p-6 space-y-5" data-testid="control-tower-page">
      <div>
        <div className="label-eyebrow mb-1">Supply Chain</div>
        <h2 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><LayoutDashboard className="h-6 w-6 text-accent" /> Supply-Chain Control Tower</h2>
        <p className="text-muted-foreground text-sm mt-1 max-w-3xl">One operating picture across engagement, data quality, disclosures, risk and CBAM. Carbon performance and data confidence are always shown separately.</p>
      </div>

      {ct.empty ? <div className="card-flat p-10 text-center text-muted-foreground">No supplier data yet.</div> : (
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview" data-testid="ct-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="scoring" data-testid="ct-tab-scoring">Explainable Scoring</TabsTrigger>
            <TabsTrigger value="risk" data-testid="ct-tab-risk">Risk / CBAM Split</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <Stat testid="ct-suppliers" label="Suppliers" value={ct.suppliers.total} sub={`S$${(ct.suppliers.spend_sgd || 0).toLocaleString()} spend`} />
              <Stat testid="ct-pcf" label="Primary PCF" value={`${ct.suppliers.with_primary_pcf_pct}%`} sub={`${ct.suppliers.with_primary_pcf} on tier A/B`} />
              <Stat testid="ct-avg" label="Avg performance" value={ct.scoring.avg_overall} sub="explainable score" />
              <Stat testid="ct-conf" label="Avg data confidence" value={ct.scoring.avg_data_confidence ?? "—"} sub="separate indicator" />
              <Stat testid="ct-campaigns" label="Active campaigns" value={ct.campaigns.active} sub={`${ct.campaigns.suppliers_targeted} targeted`} />
              <Stat testid="ct-cbam" label="CBAM lines" value={ct.risk.cbam_lines} sub="separate domain" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="card-flat p-5">
                <div className="label-eyebrow mb-2 flex items-center gap-2"><Gauge className="h-4 w-4 text-accent" /> Performance bands</div>
                <Dist dist={ct.scoring.distribution} />
                <div className="flex gap-4 text-[11px] mt-2 font-mono">
                  <span className="text-[#00E676]">● {ct.scoring.distribution.green} green</span>
                  <span className="text-[#FFB800]">● {ct.scoring.distribution.amber} amber</span>
                  <span className="text-[#FF5252]">● {ct.scoring.distribution.red} red</span>
                </div>
              </div>
              <div className="card-flat p-5">
                <div className="label-eyebrow mb-2">Disclosure lifecycle</div>
                <div className="space-y-1.5">
                  {Object.entries(ct.disclosures.funnel).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between text-[11px]">
                      <span className="capitalize text-muted-foreground">{k.replace(/_/g, " ")}</span>
                      <span className="font-mono font-bold">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="card-flat p-5">
                <div className="label-eyebrow mb-2">Action plans</div>
                <div className="font-display text-3xl font-bold">{ct.action_plans.avg_progress}%</div>
                <div className="text-[10px] text-muted-foreground mb-2">avg progress · {ct.action_plans.total} plan(s)</div>
                <div className="flex gap-3 text-[11px] font-mono">
                  <span>{ct.action_plans.status.open || 0} open</span>
                  <span>{ct.action_plans.status.in_progress || 0} in-progress</span>
                  <span>{ct.action_plans.status.complete || 0} done</span>
                </div>
              </div>
            </div>

            <div className="card-flat p-5">
              <div className="label-eyebrow mb-3 flex items-center gap-2"><Target className="h-4 w-4 text-accent" /> Top engagement priorities</div>
              <div className="space-y-2">
                {ct.top_priorities.map((p) => (
                  <div key={p.supplier_id} className="flex items-center gap-3 flex-wrap p-3 rounded-sm border border-border bg-surface" data-testid={`ct-priority-${p.supplier_id}`}>
                    <div className="font-mono text-lg font-bold w-12">{p.priority_score}</div>
                    <div className="flex-1 min-w-[180px]">
                      <div className="text-sm font-medium">{p.supplier_name}</div>
                      <div className="text-[10px] text-muted-foreground">{p.why.join(" · ")}</div>
                    </div>
                    <Badge variant={BAND[p.band]} className="text-[8px] uppercase">{p.band}</Badge>
                  </div>
                ))}
              </div>
            </div>
            <div className="card-flat p-3 text-[11px] text-muted-foreground">{ct.note}</div>
          </TabsContent>

          <TabsContent value="scoring" className="mt-4"><ScoringTab /></TabsContent>
          <TabsContent value="risk" className="mt-4"><RiskCbamTab /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
